import { defineStore } from 'pinia'
import type { TTurnaroundTime } from '@team-milva/lab-models/resources'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import { db } from '@/firebase/init'
import { managementApi } from '@/globals/typescript/utils/api/managementClient'
import { useAppStore } from '@/stores/appStore'
import { useAccountStore } from '@/stores/accountStore'
import { TurnaroundTime } from '@/globals/typescript/models/TurnaroundTime'
import { compareTurnaroundTimes } from '@/globals/typescript/utils/tatSortOrder'

export { TurnaroundTime }

type CreateTATParams =
  | {
      type: 'fixed-deadline'
      workingDays: number
      deadline: { hour: number; minute: number }
    }
  | { type: 'walk-in'; durationMode: 'hours'; name: string; durationHours: number }
  | { type: 'walk-in'; durationMode: 'same-day'; name: string }

type UpdateTATParams =
  | {
      type: 'fixed-deadline'
      workingDays: number
      deadline: { hour: number; minute: number }
    }
  | { type: 'walk-in'; durationMode: 'hours'; name: string; durationHours: number }
  | { type: 'walk-in'; durationMode: 'same-day'; name: string }

export const useTurnaroundTimeStore = defineStore('turnaroundTime', {
  state: () => ({
    allTATs: [] as TurnaroundTime[],
    unsubscribeAllTATs: null as (() => void) | null,
  }),

  getters: {
    allTATsLoaded(): boolean {
      return !!this.unsubscribeAllTATs
    },
  },

  actions: {
    getTATs(): Promise<TurnaroundTime[]> {
      if (this.allTATsLoaded) {
        return Promise.resolve(this.allTATs)
      }

      return new Promise((resolve) => {
        let resolved = false

        const accountStore = useAccountStore()

        const q = query(
          collection(db, 'Apps', '-lab-management-', 'TATs'),
          where('accountID', '==', accountStore.labAccount?.id)
        )

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          this.allTATs = querySnapshot.docs
            .map((doc) => new TurnaroundTime(doc.data() as TTurnaroundTime))
            // The canonical order for every list and dropdown — walk-ins by
            // name, then deadlines by working days and cut-off time. Sorting
            // here means consumers inherit it without re-deriving it.
            .sort((a, b) =>
              compareTurnaroundTimes(a, b, useAppStore().currentLocale)
            )
          this.unsubscribeAllTATs = unsubscribe

          if (!resolved) {
            resolved = true
            resolve(this.allTATs)
          }
        })
      })
    },

    async createTAT(params: CreateTATParams): Promise<void> {
      const { currentLocale } = useAppStore()
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      if (params.type === 'fixed-deadline') {
        const result = await managementApi('post', 'lab-accounts/{labAccountID}/tats', {
          path: { labAccountID },
          body: {
            type: 'fixed-deadline',
            workingDays: params.workingDays,
            deadline: params.deadline,
          },
        })
        if (!result.success) throw new Error(result.code)
      } else {
        const result = await managementApi('post', 'lab-accounts/{labAccountID}/tats', {
          path: { labAccountID },
          body: {
            type: 'walk-in',
            name: { [currentLocale]: params.name },
            durationMode: params.durationMode,
            ...(params.durationMode === 'hours'
              ? { durationHours: params.durationHours }
              : {}),
          },
        })
        if (!result.success) throw new Error(result.code)
      }
    },

    async updateTAT(tatID: string, params: UpdateTATParams): Promise<void> {
      const { currentLocale } = useAppStore()
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      if (params.type === 'fixed-deadline') {
        const result = await managementApi(
          'patch',
          'lab-accounts/{labAccountID}/tats/{tatID}',
          {
            path: { labAccountID, tatID },
            body: {
              type: 'fixed-deadline',
              workingDays: params.workingDays,
              deadline: params.deadline,
            },
          }
        )
        if (!result.success) throw new Error(result.code)
      } else {
        const result = await managementApi(
          'patch',
          'lab-accounts/{labAccountID}/tats/{tatID}',
          {
            path: { labAccountID, tatID },
            body: {
              type: 'walk-in',
              name: { [currentLocale]: params.name },
              durationMode: params.durationMode,
              ...(params.durationMode === 'hours'
                ? { durationHours: params.durationHours }
                : {}),
            },
          }
        )
        if (!result.success) throw new Error(result.code)
      }
    },

    // Hydrates from a Display's public data endpoint — see orderStore.ts.
    hydrateFromPublicData(raw: unknown[]): void {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.allTATs = (raw as any[]).map((x) => new TurnaroundTime(x))
    },

    resetStore(): void {
      this.unsubscribeAllTATs?.()
      this.allTATs = []
      this.unsubscribeAllTATs = null
    },
  },
})

